import './maps.less'

import {View, createContext, m} from '@codeurs/front'

import loadGoogleMapsApi from 'load-google-maps-api'

const MapsContext = createContext<google.maps.Map>()

class MapPool {
	pool: Array<google.maps.Map> = []

	create(construct: (dom: Element) => google.maps.Map) {
		const container = () => {
			const dom = document.createElement('div')
			dom.style.height = '100%'
			return dom
		}
		const instance = this.pool.length ? this.pool.pop() : construct(container())
		return instance
	}

	release(map: google.maps.Map) {
		const dom = map.getDiv()
		if (dom) dom.parentNode.removeChild(dom)
		this.pool.push(map)
	}
}

type MouseEvents = {
	click?: (event?: google.maps.MouseEvent) => void
	dblclick?: (event?: google.maps.MouseEvent) => void
	rightclick?: (event?: google.maps.MouseEvent) => void
	mousemove?: (event?: google.maps.MouseEvent) => void
	mouseout?: (event?: google.maps.MouseEvent) => void
	mouseover?: (event?: google.maps.MouseEvent) => void
}

type MapsEvents = MouseEvents & {
	idle?: () => void
	tilesloaded?: () => void

	drag?: () => void
	dragend?: () => void
	dragstart?: () => void

	bounds_changed?: () => void
	center_changed?: () => void
	heading_changed?: () => void
	maptypeid_changed?: () => void
	projection_changed?: () => void
	tilt_changed?: () => void
	zoom_changed?: () => void
}

class EventCache {
	obj: google.maps.MVCObject
	fetchListener: (key: string) => Function
	attached = new Map()
	constructor(
		obj: google.maps.MVCObject,
		fetchListener: (key: string) => Function
	) {
		this.obj = obj
		this.fetchListener = fetchListener
	}
	attach(events: Array<string>) {
		events.forEach(key => {
			if (this.attached.has(key)) return
			this.attached.set(
				key,
				this.obj.addListener(key, (event = {}) => {
					event.redraw = true
					this.fetchListener(key)(event)
					if (event.redraw) m.redraw()
				})
			)
		})
		this.remove(
			Array.from(this.attached.keys()).filter(key => events.indexOf(key) === -1)
		)
	}
	remove(selected?: Array<string>) {
		Array.from(this.attached.keys())
			.filter(key => !selected || selected.indexOf(key) > -1)
			.forEach(event => {
				google.maps.event.removeListener(this.attached.get(event))
				this.attached.delete(event)
			})
	}
}

class EventManager extends View<{
	object: google.maps.MVCObject
	on?: {[key: string]: Function}
}> {
	render = this.render
	events: EventCache
	onInit() {
		const {object} = this.attrs
		this.events = new EventCache(object, event => this.attrs.on[event])
	}
	onRemove() {
		this.events.remove()
	}
	view() {
		const {on} = this.attrs
		on && this.events.attach(Object.keys(on))
		return this.children
	}
}

export class Maps extends View<
	{
		apiKey: string
		region?: string
		language?: string
		class?: string
		on?: MapsEvents
	} & google.maps.MapOptions
> {
	render = this.render
	static pool = new MapPool()
	map: google.maps.Map

	onCreate() {
		const {
			children,
			on,
			apiKey,
			region,
			language,
			class: className,
			...options
		} = this.attrs
		loadGoogleMapsApi({key: apiKey, region, language})
			.then(maps => {
				const map = Maps.pool.create(dom => new maps.Map(dom))
				map.setOptions({
					zoom: 7,
					center: new maps.LatLng(51.0030477, 4.5000955),
					...options
				})
				const mapDiv = map.getDiv()
				if (mapDiv) this.dom.appendChild(mapDiv)
				this.onRemove = () => {
					Maps.pool.release(map)
				}
				this.map = map
			})
			.then(m.redraw)
			.catch(console.error)
	}

	view() {
		const {class: className, on, ...options} = this.attrs
		if (this.map) {
			if (options) this.map.setOptions(options)
		}
		return m('.maps',
			{className},
			this.map &&
				m(MapsContext.Provider,
					{value: this.map},
					m(EventManager,
						{
							on,
							object: this.map
						},
						this.children
					)
				)
		)
	}
}

type MarkerEvents = MouseEvents & {
	mousedown?: (event?: google.maps.MouseEvent) => void
	mouseup?: (event?: google.maps.MouseEvent) => void

	drag?: (event?: google.maps.MouseEvent) => void
	dragend?: (event?: google.maps.MouseEvent) => void
	dragstart?: (event?: google.maps.MouseEvent) => void

	draggable_changed?: () => void
	animation_changed?: () => void
	clickable_changed?: () => void
	cursor_changed?: () => void
	flat_changed?: () => void
	icon_changed?: () => void
	position_changed?: () => void
	shape_changed?: () => void
	title_changed?: () => void
	visible_changed?: () => void
	zindex_changed?: () => void
}

export class Marker extends View<
	google.maps.MarkerOptions & {on?: MarkerEvents}
> {
	render = this.render
	marker: google.maps.Marker
	onRemove() {
		this.marker.setMap(null)
	}
	view() {
		const {children, on, ...options} = this.attrs
		return m(MapsContext.Consumer, map => {
			if (this.marker) this.marker.setOptions(options)
			else this.marker = new google.maps.Marker({map, ...options})
			return m(EventManager,
				{
					on,
					object: this.marker
				},
				this.children
			)
		})
	}
}
